import { useState } from "react";
import { supabase } from "@/lib/supabase";

type Provider = "google" | "apple";

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="#ffffff">
      <path d="M17.05 12.04c-.03-2.93 2.39-4.34 2.5-4.41-1.36-1.99-3.49-2.27-4.24-2.29-1.8-.18-3.52 1.06-4.44 1.06-.93 0-2.34-1.04-3.86-1-1.98.03-3.82 1.15-4.84 2.92-2.07 3.58-.53 8.87 1.49 11.78.98 1.42 2.15 3.02 3.68 2.96 1.49-.06 2.05-.96 3.85-.96 1.8 0 2.31.96 3.88.93 1.6-.03 2.61-1.45 3.59-2.88 1.13-1.65 1.6-3.26 1.62-3.34-.03-.02-3.11-1.19-3.14-4.77zM14.13 3.4c.82-1 1.37-2.39 1.22-3.77-1.18.05-2.61.79-3.46 1.78-.76.88-1.43 2.29-1.25 3.64 1.32.1 2.66-.67 3.49-1.65z" />
    </svg>
  );
}

export function NanaeOAuthButtons({ label }: { label: "signin" | "signup" }) {
  const [loading, setLoading] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClick = async (provider: Provider) => {
    setError(null);
    setLoading(provider);
    try {
      const redirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined;
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo },
      });
      if (error) throw error;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connexion impossible");
      setLoading(null);
    }
  };

  const verb = label === "signup" ? "S'inscrire avec" : "Continuer avec";

  const btn: React.CSSProperties = {
    height: 42,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    background: "transparent",
    border: "1px solid var(--qv3-grey-100, rgba(255,255,255,0.18))",
    color: "var(--qv3-ink, #ffffff)",
    fontFamily: '"DM Mono", monospace',
    fontSize: 11,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "background 140ms ease, border-color 140ms ease",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
      <button
        type="button"
        onClick={() => onClick("google")}
        disabled={loading !== null}
        style={{ ...btn, opacity: loading && loading !== "google" ? 0.45 : 1 }}
        aria-label={`${verb} Google`}
      >
        <GoogleIcon />
        <span>{loading === "google" ? "Redirection…" : `${verb} Google`}</span>
      </button>
      {/* Apple Sign-In masqué pour le lancement — provider non activé côté Supabase.
          Réactiver en restaurant ce bloc une fois le provider configuré. */}
      {false && (
        <button
          type="button"
          onClick={() => onClick("apple")}
          disabled={loading !== null}
          style={{ ...btn, opacity: loading && loading !== "apple" ? 0.45 : 1 }}
          aria-label={`${verb} Apple`}
        >
          <AppleIcon />
          <span>{loading === "apple" ? "Redirection…" : `${verb} Apple`}</span>
        </button>
      )}

      {error && (
        <p role="alert" className="nanae-auth-error" style={{ marginTop: 4 }}>
          <span className="qv3-led" aria-hidden="true" />
          {error}
        </p>
      )}

      <div
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 6,
          fontFamily: '"DM Mono", monospace',
          fontSize: 9,
          letterSpacing: "2.5px",
          textTransform: "uppercase",
          color: "var(--qv3-grey-300, rgba(255,255,255,0.55))",
        }}
      >
        <span style={{ flex: 1, height: 1, background: "var(--qv3-grey-100, rgba(255,255,255,0.18))" }} />
        <span>ou avec votre email</span>
        <span style={{ flex: 1, height: 1, background: "var(--qv3-grey-100, rgba(255,255,255,0.18))" }} />
      </div>
    </div>
  );
}
